import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const GroupContext = createContext(null);

// The group a link chose (`?group=`), if any.
const groupInLink = () => {
  try { return new URLSearchParams(window.location.search).get('group'); } catch { return null; }
};

export const GroupProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [groups, setGroups] = useState([]);
  // Persist group selection across navigation. Restore from sessionStorage so
  // a page refresh keeps you in the same group context.
  //
  // A LINK CAN CHOOSE IT: dev.oS opens a group's app as /applications/<id>?group=<group>,
  // in a new tab that has no group of its own, where the app would not be found among
  // your personal ones. Read first, and again once the person is known (below), since
  // signing in on the way clears it.
  const [activeGroupId, setActiveGroupId] = useState(() => {
    try { return groupInLink() || sessionStorage.getItem('activeGroupId') || null; } catch { return null; }
  });

  // Persist to sessionStorage whenever it changes
  useEffect(() => {
    try {
      if (activeGroupId) sessionStorage.setItem('activeGroupId', activeGroupId);
      else sessionStorage.removeItem('activeGroupId');
    } catch { /* ignore */ }
  }, [activeGroupId]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchGroups = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await api.getGroups();
      setGroups(data.groups || []);
    } catch (err) {
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch groups when user changes.
  //
  // The null-user branch must wait for auth to settle. AuthContext starts at
  // `user: null, loading: true` and only resolves once the stored token has
  // been validated, so on every refresh this effect used to run once with no
  // user, read that as a logout, and clear activeGroupId — which the effect
  // above then erased from sessionStorage. The restored group was wiped before
  // anyone had established whether the person was logged out at all, and the
  // page always came back in personal.
  useEffect(() => {
    if (user) {
      const linked = groupInLink();
      if (linked) setActiveGroupId(linked);
      fetchGroups();
      return;
    }
    if (authLoading) return;   // not "logged out" — "not known yet"
    setGroups([]);
    setActiveGroupId(null);
    setActiveGroup(null);
  }, [user, authLoading, fetchGroups]);

  // Update activeGroup when activeGroupId changes
  useEffect(() => {
    if (activeGroupId) {
      const group = groups.find(g => g.id === activeGroupId);
      setActiveGroup(group || null);
    } else {
      setActiveGroup(null);
    }
  }, [activeGroupId, groups]);

  const switchToPersonal = useCallback(() => {
    setActiveGroupId(null);
  }, []);

  const switchToGroup = useCallback((groupId) => {
    setActiveGroupId(groupId);
  }, []);

  const value = {
    groups,
    activeGroupId,
    activeGroup,
    loading,
    isGroupContext: !!activeGroupId,
    switchToPersonal,
    switchToGroup,
    refreshGroups: fetchGroups,
  };

  return <GroupContext.Provider value={value}>{children}</GroupContext.Provider>;
};

export const useGroup = () => {
  const context = useContext(GroupContext);
  if (!context) {
    throw new Error('useGroup must be used within GroupProvider');
  }
  return context;
};
